import { tempoApi } from '@/lib/api/tempoApi';
import type {
  Scenario,
  ScenarioActivationResponse,
  ScenarioAlert,
  ScenarioChangeSummary,
  ScenarioConstraintsScale,
  ScenarioResponse,
} from '@/lib/api/types/loop-planner/scenario';

const createScenario = async ({ loopId, name }: { loopId: number; name: string }) => {
  return tempoApi()
    .post(`api/v1/adaptive-planner/loops/${loopId}/scenarios`, {
      json: { name },
    })
    .json<Scenario>();
};

const getScenarios = async ({ loopId }: { loopId: number }) => {
  return tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios`)
    .json<ScenarioResponse[]>();
};

const getScenario = async ({
  loopId,
  scenarioId,
  includeMetrics = false,
}: {
  loopId: number;
  scenarioId: number;
  includeMetrics?: boolean;
}) => {
  return tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}`, {
      searchParams: { includeMetrics: String(includeMetrics) },
    })
    .json<ScenarioResponse>();
};

export interface UpdateScenarioRequest {
  name?: string;
  priorityFlexibility?: ScenarioConstraintsScale;
  capacityUtilization?: ScenarioConstraintsScale;
  estimationBuffer?: ScenarioConstraintsScale;
}

const updateScenario = async ({
  loopId,
  scenarioId,
  data,
}: {
  loopId: number;
  scenarioId: number;
  data: UpdateScenarioRequest;
}) => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}`, {
      json: data,
    })
    .json<Scenario>();
  return response;
};

const getAlerts = async ({
  loopId,
  scenarioId,
  alertTypes,
}: {
  loopId: number;
  scenarioId: number;
  alertTypes?: ScenarioAlert['alertType'][];
}): Promise<ScenarioAlert[]> =>
  tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/alerts`, {
      searchParams: new URLSearchParams(alertTypes?.map((type) => ['alertTypes', type])),
    })
    .json<ScenarioAlert[]>();

const getChangeSummary = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<ScenarioChangeSummary> =>
  tempoApi()
    .get(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/change-summary`)
    .json<ScenarioChangeSummary>();

const loopSync = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi()
    .post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/loop-sync`)
    .json();
};

const deleteScenario = async ({ loopId, scenarioId }: { loopId: number; scenarioId: number }) => {
  await tempoApi().delete(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}`);
};

const resetScenario = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/reset`);
};

const schedule = async ({
  loopId,
  scenarioId,
}: {
  loopId: number;
  scenarioId: number;
}): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/schedule`);
};

const activate = async ({
  loopId,
  scenarioId,
  pushToJira,
}: {
  loopId: number;
  scenarioId: number;
  pushToJira: boolean;
}): Promise<ScenarioActivationResponse> => {
  return tempoApi()
    .post(`api/v1/adaptive-planner/loops/${loopId}/scenarios/${scenarioId}/activate`, {
      searchParams: { pushToJira: String(pushToJira) },
    })
    .json<ScenarioActivationResponse>();
};

export const LoopPlannerScenarioApi = {
  createScenario,
  getScenarios,
  getScenario,
  updateScenario,
  getAlerts,
  getChangeSummary,
  loopSync,
  deleteScenario,
  resetScenario,
  schedule,
  activate,
} as const;
